import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import 'es5-shim';
import 'es5-shim/es5-sham';
import 'babel-polyfill';

import Map from './Map';
import LocationsList from './LocationsList';
import Search from './Search';
import Loading from './Loading';
import formatCoordinate from './helpers/formatCoordinate';

class LocationsFinder extends Component {
    constructor(props) {
        super(props);

        const zoomLevels = {
            default: props.zoomLevel || 5,
            detail: 18,
        };

        // set translations global
        window.map_translations = props.translations;

        this.state = {
            locations: props.locations,
            filteredLocations: props.locations,
            activeMarker: 0,
            mapCenter: null,
            searchRadius: 1,
            zoomLevels,
            zoom: zoomLevels.default,
            loading: false,
        };

        this.toggleLocation = this.toggleLocation.bind(this);
        this.locate = this.locate.bind(this);
        this.search = this.search.bind(this);
        this.centerMap = this.centerMap.bind(this);
        this.setLoading = this.setLoading.bind(this);
        this.setZoom = this.setZoom.bind(this);
        this.updateList = this.updateList.bind(this);
        this.addCurrentLocation = this.addCurrentLocation.bind(this);
    }

    toggleLocation(id) {
        if (this.state.activeMarker === id) {
            // close current infowindow
            this.setState(prevState => ({
                activeMarker: 0,
                locations: prevState.locations.map((location) => {
                    return {
                        ...location,
                        open: false,
                    };
                }),
            }));
        } else {
            // open clicked infowindow
            this.setState(prevState => ({
                activeMarker: id,
                locations: prevState.locations.map((location) => {
                    if (location.id !== id) {
                        return {
                            ...location,
                            open: false,
                        };
                    } else {
                        return {
                            ...location,
                            open: true,
                        };
                    }
                }),
            }));

            const location = this.state.locations.find(l => l.id === id);
            const position = new window.google.maps.LatLng(
                formatCoordinate(location.lat),
                formatCoordinate(location.lng),
            );
            if (this.state.zoom < this.state.zoomLevels.detail) {
                // don't center & zoom when already close in on marker. otherwise the cluster view can be triggered
                this.setZoom(this.state.zoomLevels.detail);
            }
            this.centerMap(position);
        }
    }

    getNearestLocations(currentLocation) {
        // https://stackoverflow.com/questions/10295371/finding-all-the-markers-inside-a-given-radius
        // https://stackoverflow.com/questions/4057665/window.google-maps-api-v3-find-nearest-markers
        const nearestLocations = this.props.locations.filter((item) => {
            const position = new window.google.maps.LatLng(
                formatCoordinate(item.lat),
                formatCoordinate(item.lng),
            );
            const distance = window.google.maps.geometry.spherical.computeDistanceBetween(
                currentLocation,
                position,
                this.state.searchRadius * 1000, // radius in km
            );

            if (distance < this.state.searchRadius) {
                return true;
            }
            return false;
        });

        return nearestLocations;
    }

    search(searchTerm) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
            {
                address: searchTerm,
            },
            (results, status) => {
                if (status === window.google.maps.GeocoderStatus.OK) {
                    const { location } = results[0].geometry;
                    this.centerMap(location);
                    this.updateList(location);
                }
            },
        );
    }

    locate(currentLocation) {
        this.setLoading(false);
        this.updateList(currentLocation);
        this.addCurrentLocation(currentLocation);
    }

    addCurrentLocation(currentLocation) {
        this.setState({
            locations: [
                ...this.props.locations,
                {
                    id: 1234567890,
                    lat: currentLocation.lat(),
                    lng: currentLocation.lng(),
                },
            ],
        });
    }

    centerMap(currentLocation) {
        this.setState({
            mapCenter: currentLocation,
        });
    }

    updateList(currentLocation) {
        const nearestLocations = this.getNearestLocations(currentLocation).sort((a, b) => {
            return a.distance - b.distance;
        });

        this.setState({
            filteredLocations: nearestLocations,
        });
    }

    setLoading(loading) {
        this.setState({ loading });
    }

    setZoom(zoom) {
        this.setState({
            zoom,
        });
    }

    render() {
        return (
            <div>
                {this.props.enableSearch && (
                    <Search
                        key="search"
                        search={this.search}
                        locate={this.locate}
                        setLoading={this.setLoading}
                        loading={this.state.loading}
                    />
                )}
                <div className="location-finder__map-container">
                    <Map
                        key="map"
                        googleMapURL="https://maps.googleapis.com/maps/api/js?key=AIzaSyCjsTS-uAlxS7LCfiY6HdVE6h2HPoLzByg&v=3.exp&libraries=geometry,drawing,places"
                        loadingElement={<div style={{ height: '100%' }} />}
                        containerElement={<div className="locations-map" />}
                        mapElement={<div style={{ height: '100%' }} />}
                        locations={this.state.locations}
                        filteredLocations={this.state.filteredLocations}
                        toggleLocation={this.toggleLocation}
                        setZoom={this.setZoom}
                        center={this.state.mapCenter}
                        zoom={this.state.zoom}
                        fitOffset={0.005} // 5m
                        enableClusters={this.props.enableClusters}
                        enableList={this.props.enableList}
                    />
                    <LocationsList
                        key="locations-list"
                        locations={this.state.filteredLocations}
                    />
                </div>
                <div>
                    {this.props.enableSearch && (
                        <Loading loading={this.state.loading} />
                    )}
                </div>
            </div>
        );
    }
}

LocationsFinder.defaultProps = {
    zoomLevel: 5,
    enableSearch: false,
    enableList: false,
    enableClusters: false,
};

LocationsFinder.propTypes = {
    zoomLevel: PropTypes.number,
    locations: PropTypes.arrayOf(PropTypes.object).isRequired,
    enableSearch: PropTypes.bool,
    enableList: PropTypes.bool,
    enableClusters: PropTypes.bool,
};

const $locationFinderMount = document.getElementById('location-finder');

if ($locationFinderMount) {
    ReactDOM.render(
        <LocationsFinder
            locations={window.map_locations.locations}
            zoomLevel={window.map_locations.zoomLevel}
            enableSearch
            // enableClusters
        />,
        $locationFinderMount,
    );
}



// WEBPACK FOOTER //
// ./static/js/modules/locations-finder/index.js